import { useId, useState } from "react";
import type { PropertyMetricRow, PropertyStatsResponse, StatsRange } from "@townsy/contracts";
import { errorMessage } from "../api/client";
import { usePropertyStatsQuery } from "../api/queries";
import { formatMetric, formatUtcDateTime } from "../lib/format";

const RANGES: Array<{ key: StatsRange; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7d", label: "7 days" },
  { key: "last30d", label: "30 days" },
  { key: "lifetime", label: "Lifetime" },
];

const METRICS: Array<{ key: keyof PropertyMetricRow; label: string; hint: string }> = [
  { key: "signImpressions", label: "Sign impressions", hint: "Filtered estimate: sign ≥50% visible for 1 s in a foreground tab, deduped per visitor per 30 min." },
  { key: "panelViews", label: "Panel views", hint: "Intentional opening of this listing, deduped for rapid repeats." },
  { key: "roomEntries", label: "Room entries", hint: "Authorised entries into the interior; reconnects are not entries." },
  { key: "outboundClicksOrdinary", label: "Website clicks", hint: "Eligible intentional clicks on Visit website — not proof the page loaded." },
  { key: "outboundClicksRewarded", label: "Rewarded clicks", hint: "Clicks tied to a disclosed sponsored attempt (none in this milestone)." },
];

/** Current-owner vs lifetime metrics for one property (docs/01 section 6, docs/07 section 7). */
export function PropertyStats({ propertyId, active }: { propertyId: string; active: boolean }) {
  const [tab, setTab] = useState<"currentOwner" | "lifetime">("currentOwner");
  const query = usePropertyStatsQuery(propertyId, active);
  const tabsId = useId();
  if (!active) return null;
  if (query.isPending) return <p className="muted small">Loading statistics…</p>;
  if (query.error) {
    return (
      <p className="small">
        <span className="error-text">Statistics unavailable:</span> {errorMessage(query.error)}{" "}
        <button type="button" className="btn btn-sm" onClick={() => void query.refetch()}>
          Retry
        </button>
      </p>
    );
  }
  const data: PropertyStatsResponse = query.data;
  const cell = (range: StatsRange, metric: keyof PropertyMetricRow): string => {
    const r = data.ranges[range];
    if (!r) return "—";
    const row = tab === "lifetime" ? r.lifetime : r.currentOwner;
    if (!row) return "—";
    return formatMetric(row[metric]);
  };
  return (
    <div>
      <div className="tabs" role="tablist" aria-label="Statistics scope">
        <button
          type="button"
          role="tab"
          id={`${tabsId}-owner`}
          className="tab"
          aria-selected={tab === "currentOwner"}
          onClick={() => setTab("currentOwner")}
        >
          Current owner
        </button>
        <button type="button" role="tab" id={`${tabsId}-lifetime`} className="tab" aria-selected={tab === "lifetime"} onClick={() => setTab("lifetime")}>
          Lifetime
        </button>
      </div>
      <div role="tabpanel" aria-labelledby={tab === "lifetime" ? `${tabsId}-lifetime` : `${tabsId}-owner`}>
        {tab === "currentOwner" ? (
          <p className="small muted">
            {data.currentOwnerSince
              ? `Activity since the current ownership began (${formatUtcDateTime(data.currentOwnerSince)}). Earlier owners' traffic is not shown here.`
              : "No current ownership interval: this building is unowned, so current-owner metrics are shown as dashes."}
          </p>
        ) : (
          <p className="small muted">All measured activity at this location across every owner.</p>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <caption className="visually-hidden">Property metrics by range (UTC days)</caption>
            <thead>
              <tr>
                <th scope="col">Metric</th>
                {RANGES.map((r) => (
                  <th key={r.key} scope="col" className="num">
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((m) => (
                <tr key={m.key}>
                  <th scope="row" title={m.hint}>
                    {m.label}
                  </th>
                  {RANGES.map((r) => {
                    const v = cell(r.key, m.key);
                    return (
                      <td key={r.key} className={`num${v === "—" ? " dash" : ""}`} title={v === "—" ? "Not available for this range" : undefined}>
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <details className="notes" style={{ marginTop: 8 }}>
          <summary className="small">How we count</summary>
          <ul>
            {data.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
            <li>Ranges use UTC days. Dashes mean the measurement is not available for that range.</li>
          </ul>
        </details>
        <p className="faint small" style={{ marginTop: 6 }}>
          Updated {formatUtcDateTime(data.generatedAt)} · definitions v{data.definitionsVersion}
          {data.lastAggregatedAt ? ` · aggregated ${formatUtcDateTime(data.lastAggregatedAt)}` : ""}
        </p>
      </div>
    </div>
  );
}
